/**
 * VirtualKeyboard Component
 * Physically proportional piano keyboard for testing without a hardware MIDI controller.
 * Proportional ivory & ebony key layout, authentic key caps & computer keyboard triggers.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { globalMidiAccess } from '../core/midiAccess';
import { getPitchName, isBlackKey } from '../core/timing';
import { detectRealtimeChord } from '../core/chordDetector';
import { ChevronUp, ChevronDown, Keyboard, X } from 'lucide-react';

interface KeyMapping {
  key: string;
  semitoneOffset: number; // relative to base C
}

export interface VirtualKeyboardProps {
  onClose?: () => void;
}

const COMPUTER_KEY_MAP: KeyMapping[] = [
  { key: 'a', semitoneOffset: 0 },  // C
  { key: 'w', semitoneOffset: 1 },  // C#
  { key: 's', semitoneOffset: 2 },  // D
  { key: 'e', semitoneOffset: 3 },  // D#
  { key: 'd', semitoneOffset: 4 },  // E
  { key: 'f', semitoneOffset: 5 },  // F
  { key: 't', semitoneOffset: 6 },  // F#
  { key: 'g', semitoneOffset: 7 },  // G
  { key: 'y', semitoneOffset: 8 },  // G#
  { key: 'h', semitoneOffset: 9 },  // A
  { key: 'u', semitoneOffset: 10 }, // A#
  { key: 'j', semitoneOffset: 11 }, // B
  { key: 'k', semitoneOffset: 12 }, // C (octave up)
  { key: 'o', semitoneOffset: 13 }, // C#
  { key: 'l', semitoneOffset: 14 }, // D
  { key: 'p', semitoneOffset: 15 }, // D#
];

export const VirtualKeyboard: React.FC<VirtualKeyboardProps> = ({ onClose }) => {
  const [baseOctave, setBaseOctave] = useState<number>(4); // C4 = 60
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [activePitches, setActivePitches] = useState<Set<number>>(new Set());

  const basePitch = baseOctave * 12 + 12; // Octave 4 -> 60 (Middle C)
  const activePitchesRef = useRef<Set<number>>(new Set());
  activePitchesRef.current = activePitches;

  // Real-time chord detected from active virtual keyboard keys
  const activeChord = useMemo(() => {
    if (activePitches.size === 0) return null;
    return detectRealtimeChord(Array.from(activePitches));
  }, [activePitches]);

  const noteOn = useCallback((pitch: number) => {
    setActivePitches((prev) => {
      const next = new Set(prev);
      next.add(pitch);
      return next;
    });
    globalMidiAccess.triggerVirtualNoteOn(pitch, 96);
  }, []);

  const noteOff = useCallback((pitch: number) => {
    setActivePitches((prev) => {
      const next = new Set(prev);
      next.delete(pitch);
      return next;
    });
    globalMidiAccess.triggerVirtualNoteOff(pitch);
  }, []);

  // Computer keyboard listeners
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      const mapping = COMPUTER_KEY_MAP.find((m) => m.key === e.key.toLowerCase());
      if (mapping) {
        const pitch = basePitch + mapping.semitoneOffset;
        if (!activePitchesRef.current.has(pitch)) {
          noteOn(pitch);
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      const mapping = COMPUTER_KEY_MAP.find((m) => m.key === e.key.toLowerCase());
      if (mapping) {
        const pitch = basePitch + mapping.semitoneOffset;
        noteOff(pitch);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [basePitch, noteOn, noteOff]);

  // Sync with incoming physical MIDI Keyboard Note On / Off events
  useEffect(() => {
    const unsubOn = globalMidiAccess.onNoteOn((pitch) => {
      setActivePitches((prev) => {
        if (prev.has(pitch)) return prev;
        const next = new Set(prev);
        next.add(pitch);
        return next;
      });
    });

    const unsubOff = globalMidiAccess.onNoteOff((pitch) => {
      setActivePitches((prev) => {
        if (!prev.has(pitch)) return prev;
        const next = new Set(prev);
        next.delete(pitch);
        return next;
      });
    });

    return () => {
      unsubOn();
      unsubOff();
    };
  }, []);

  // Physical piano key geometry:
  // 2 complete octaves + 1 final C note (15 white keys, 10 black keys)
  const WHITE_KEY_WIDTH = 42; // px
  const WHITE_KEY_HEIGHT = 116; // px
  const BLACK_KEY_WIDTH = 26; // px
  const BLACK_KEY_HEIGHT = 72; // px

  // Semitone definitions across 2 octaves
  // Octave semitones: 0=C, 2=D, 4=E, 5=F, 7=G, 9=A, 11=B
  interface WhiteKeyInfo {
    pitch: number;
    name: string;
    shortcut?: string;
    whiteIndex: number;
  }
  interface BlackKeyInfo {
    pitch: number;
    name: string;
    shortcut?: string;
    left: number;
  }

  const whiteKeys: WhiteKeyInfo[] = [];
  const blackKeys: BlackKeyInfo[] = [];

  let whiteCount = 0;
  // 25 semitones = 2 octaves + 1 note
  for (let semitone = 0; semitone <= 24; semitone++) {
    const pitch = basePitch + semitone;
    const isBlack = isBlackKey(pitch);
    const shortcut = COMPUTER_KEY_MAP.find((m) => m.semitoneOffset === semitone)?.key.toUpperCase();
    const name = getPitchName(pitch);

    if (!isBlack) {
      whiteKeys.push({
        pitch,
        name,
        shortcut,
        whiteIndex: whiteCount,
      });
      whiteCount++;
    } else {
      // Black key sits centered on the seam before this whiteCount
      // Seam between whiteCount - 1 and whiteCount
      const seamX = whiteCount * WHITE_KEY_WIDTH;
      const left = seamX - BLACK_KEY_WIDTH / 2;
      blackKeys.push({
        pitch,
        name,
        shortcut,
        left,
      });
    }
  }

  const totalPianoWidth = whiteKeys.length * WHITE_KEY_WIDTH;

  return (
    <div id="virtual-keyboard-root" className="border-t border-[#2D2820] bg-[#181614] select-none text-[#EBE6DB]">
      {/* Bar Header */}
      <div className="flex items-center justify-between px-3 py-1 bg-[#181614] border-b border-[#2D2820] text-xs">
        <div className="flex items-center gap-2 text-[#EBE6DB]">
          <Keyboard className="w-3.5 h-3.5 text-[#F5C767]" />
          <span className="font-bold text-[#EBE6DB]">Proportional Virtual Piano</span>
          <span className="text-[#A89F91] text-[10px] hidden sm:inline">
            (Play via mouse or keyboard: A, W, S, E, D, F...)
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Active Detected Chord Display */}
          {activeChord && (
            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-[#F5C767]/15 border border-[#F5C767] shadow-xs">
              <span className="text-[#A89F91] text-[9px] uppercase font-bold tracking-wider">Chord:</span>
              <span className="font-mono font-black text-[#F5C767] text-xs">
                {activeChord.symbol}
              </span>
            </div>
          )}

          {/* Octave Shift */}
          <div className="flex items-center gap-1 bg-[#1C1A17] px-2 py-0.5 border border-[#3D362C] text-xs shadow-xs">
            <span className="text-[#A89F91] text-[10px]">Octave:</span>
            <button
              onClick={() => setBaseOctave((prev) => Math.max(1, prev - 1))}
              className="px-1.5 py-0.5 hover:bg-[#25211B] text-[#A89F91] hover:text-[#F5C767] transition-colors font-bold"
              title="Octave down"
            >
              -
            </button>
            <span className="font-mono font-bold text-[#F5C767] px-1">C{baseOctave}</span>
            <button
              onClick={() => setBaseOctave((prev) => Math.min(7, prev + 1))}
              className="px-1.5 py-0.5 hover:bg-[#25211B] text-[#A89F91] hover:text-[#F5C767] transition-colors font-bold"
              title="Octave up"
            >
              +
            </button>
          </div>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-[#25211B] text-[#A89F91] hover:text-[#F5C767] transition-colors"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>

          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-[#25211B] text-[#A89F91] hover:text-[#F5C767] transition-colors ml-1"
              title="Close virtual keyboard"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Proportional Piano Stage */}
      {isExpanded && (
        <div className="flex justify-center p-3 bg-[#13110F] overflow-x-auto border-t border-[#2D2820]">
          <div
            className="relative shadow-md p-1 bg-[#0D0B0A] border border-[#2D2820]"
            style={{ width: `${totalPianoWidth + 10}px`, height: `${WHITE_KEY_HEIGHT + 10}px` }}
          >
            {/* White Keys Row (Natural Ivory Keys) */}
            <div className="flex relative" style={{ width: `${totalPianoWidth}px`, height: `${WHITE_KEY_HEIGHT}px` }}>
              {whiteKeys.map((k) => {
                const isActive = activePitches.has(k.pitch);
                const isC = k.pitch % 12 === 0;

                return (
                  <button
                    key={k.pitch}
                    id={`virtual-key-${k.pitch}`}
                    onMouseDown={() => noteOn(k.pitch)}
                    onMouseUp={() => noteOff(k.pitch)}
                    onMouseLeave={() => {
                      if (activePitches.has(k.pitch)) noteOff(k.pitch);
                    }}
                    style={{
                      width: `${WHITE_KEY_WIDTH}px`,
                      height: `${WHITE_KEY_HEIGHT}px`,
                    }}
                    className={`relative rounded-b-[4px] border-r border-b border-l border-neutral-300/90 flex flex-col justify-end pb-2 items-center cursor-pointer transition-all ${
                      isActive
                        ? 'bg-gradient-to-b from-amber-300 via-amber-200 to-amber-100 text-amber-950 shadow-inner font-bold translate-y-[2px]'
                        : isC
                        ? 'bg-gradient-to-b from-neutral-100 via-white to-neutral-50 hover:to-neutral-100 text-neutral-900 shadow-[0_4px_6px_rgba(0,0,0,0.35)]'
                        : 'bg-gradient-to-b from-neutral-200/90 via-neutral-100 to-white hover:to-neutral-50 text-neutral-800 shadow-[0_4px_6px_rgba(0,0,0,0.3)]'
                    }`}
                    title={`${k.name} (MIDI ${k.pitch})`}
                  >
                    {/* Ivory lip bevel */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-neutral-400/20 rounded-b-[4px] pointer-events-none" />

                    {/* Note Name */}
                    <span className={`text-[10px] ${isC ? 'font-bold text-amber-700' : 'font-semibold text-neutral-700'}`}>
                      {k.name}
                    </span>

                    {/* Computer key shortcut pill */}
                    {k.shortcut && (
                      <span className="mt-0.5 text-[8px] font-mono px-1 py-0.2 rounded bg-neutral-200 text-neutral-600 font-medium">
                        {k.shortcut}
                      </span>
                    )}
                  </button>
                );
              })}

              {/* Black Keys Layer (Proportional Elevated Ebony Caps) */}
              {blackKeys.map((k) => {
                const isActive = activePitches.has(k.pitch);

                return (
                  <button
                    key={k.pitch}
                    id={`virtual-key-${k.pitch}`}
                    onMouseDown={() => noteOn(k.pitch)}
                    onMouseUp={() => noteOff(k.pitch)}
                    onMouseLeave={() => {
                      if (activePitches.has(k.pitch)) noteOff(k.pitch);
                    }}
                    style={{
                      left: `${k.left}px`,
                      width: `${BLACK_KEY_WIDTH}px`,
                      height: `${BLACK_KEY_HEIGHT}px`,
                    }}
                    className={`absolute top-0 z-20 rounded-b-[3px] flex flex-col justify-end pb-1.5 items-center cursor-pointer transition-all border border-neutral-900 ${
                      isActive
                        ? 'bg-gradient-to-b from-amber-600 via-amber-500 to-amber-600 text-black shadow-[0_0_12px_rgba(245,158,11,0.8)] translate-y-[2px]'
                        : 'bg-gradient-to-b from-neutral-900 via-neutral-800 to-neutral-950 text-neutral-300 hover:from-neutral-800 hover:to-neutral-900 shadow-[0_4px_8px_rgba(0,0,0,0.85),1px_2px_4px_rgba(0,0,0,0.7)]'
                    }`}
                    title={`${k.name} (MIDI ${k.pitch})`}
                  >
                    {/* Top specular reflection */}
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/20 rounded-t-[2px] pointer-events-none" />

                    {/* Bottom edge lip */}
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-neutral-950/80 rounded-b-[3px] pointer-events-none" />

                    <span className="text-[8px] font-mono font-medium opacity-80 pointer-events-none">
                      {k.name.replace('#', '♯')}
                    </span>

                    {k.shortcut && (
                      <span className="text-[7px] font-mono font-bold text-amber-400 mt-0.5 pointer-events-none">
                        {k.shortcut}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
